import { useState, useCallback, useEffect } from 'react';
import { UserTrack, LocationPoint, UserLocationUpdateMessage } from '../types/websocket.types';

interface TrackDataHookProps {
  sendMessage: (message: any) => void;
}


export const useTrackData = ({ sendMessage }: TrackDataHookProps) => {
  const [tracks, setTracks] = useState<UserTrack[]>([]);
  const [trackPoints, setTrackPoints] = useState<Map<string, LocationPoint[]>>(new Map());
  const [tracksGeoJSON, setTracksGeoJSON] = useState<GeoJSON.FeatureCollection | null>(null);

  // Handle incoming tracks response
  const handleTracksResponse = useCallback((newTracks: UserTrack[]) => {
    setTracks(prevTracks => {
      // Create a map of existing tracks for quick lookup
      const existingTracksMap = new Map(prevTracks.map(track => [track.id, track]));
      
      // Merge new tracks with existing ones, prioritizing server data
      const mergedTracks = newTracks.map(serverTrack => {
        const existingTrack = existingTracksMap.get(serverTrack.id);
        if (existingTrack) {
          // Merge server data with existing track, keeping server data as authoritative
          return {
            ...existingTrack,
            ...serverTrack, // Server data takes precedence
          };
        }
        return serverTrack;
      });
      
      // Add any tracks that exist locally but not on server (real-time created ones)
      const serverTrackIds = new Set(newTracks.map(track => track.id));
      const localOnlyTracks = prevTracks.filter(track => !serverTrackIds.has(track.id));
      
      return [...mergedTracks, ...localOnlyTracks];
    });
    
    // Request points for each track
    newTracks.forEach(track => {
      sendMessage({
        type: 'query_track_points',
        trackId: track.id
      });
    });
  }, [sendMessage]);

  // Handle incoming track points response
  const handleTrackPointsResponse = useCallback((trackId: string, points: LocationPoint[]) => {
    setTrackPoints(prev => {
      const newMap = new Map(prev);
      newMap.set(trackId, points);
      return newMap;
    });
  }, []);

  // Handle real-time user location updates
  const handleUserLocationUpdate = useCallback((message: UserLocationUpdateMessage) => {
    const newPoint: LocationPoint = {
      id: `${message.trackId}_${message.timestamp}`,
      latitude: message.latitude,
      longitude: message.longitude,
      timestamp: new Date(message.timestamp).toISOString(),
      track_id: message.trackId
    };

    // Update track points
    setTrackPoints(prev => {
      const newMap = new Map(prev);
      const existingPoints = newMap.get(message.trackId) || [];
      
      // Add the new point and sort by timestamp (server already filtered duplicates)
      const updatedPoints = [...existingPoints, newPoint].sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      newMap.set(message.trackId, updatedPoints);
      
      return newMap;
    });

    // Ensure the track exists in the tracks list
    setTracks(prevTracks => {
      const existingTrack = prevTracks.find(track => track.id === message.trackId);
      
      if (!existingTrack) {
        // Create a new track entry for this trackId
        const newTrack: UserTrack = {
          id: message.trackId,
          name: `Track_${message.trackId.substring(0, 8)}`, // Temporary name
          isActive: true, // Assume active since we're receiving updates
          startedAt: new Date(message.timestamp).toISOString(),
          user_id: message.userId,
          room_id: '', // Will be updated when full track info arrives
        };
        
        return [...prevTracks, newTrack];
      } else if (!existingTrack.isActive) {
        // If track exists but was marked as inactive, reactivate it
        return prevTracks.map(track => 
          track.id === message.trackId 
            ? { ...track, isActive: true, endedAt: undefined }
            : track
        );
      }
      
      return prevTracks; // No changes needed
    });
  }, []);

  // Handle when a user stops tracking (for real-time updates)
  const handleUserStoppedTracking = useCallback((userId: string) => {
    setTracks(prevTracks => {
      return prevTracks.map(track => {
        if (track.user_id === userId && track.isActive) {
          return {
            ...track,
            isActive: false,
            endedAt: new Date().toISOString()
          };
        }
        return track;
      });
    });
  }, []);

  // Convert tracks and points to GeoJSON
  useEffect(() => {
    if (tracks.length === 0) {
      setTracksGeoJSON(null);
      return;
    }

    const features: GeoJSON.Feature[] = [];

    tracks.forEach(track => {
      const points = trackPoints.get(track.id);
      if (!points || points.length === 0) return;

      // Sort points by timestamp
      const sortedPoints = [...points].sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      if (sortedPoints.length === 1) {
        // Single point - create a Point feature
        const point = sortedPoints[0];
        features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [point.longitude, point.latitude]
          },
          properties: {
            id: track.id,
            userId: track.user_id,
            trackName: track.name,
            isActive: track.isActive,
            startedAt: track.startedAt,
            endedAt: track.endedAt,
            pointCount: 1,
            type: 'track_point'
          }
        });
      } else {
        // Multiple points - create a LineString feature
        const coordinates = sortedPoints.map(point => [point.longitude, point.latitude]);
        
        features.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates
          },
          properties: {
            id: track.id,
            userId: track.user_id,
            trackName: track.name,
            isActive: track.isActive,
            startedAt: track.startedAt,
            endedAt: track.endedAt,
            pointCount: sortedPoints.length,
            type: 'track_line'
          }
        });

        // Add start point marker
        const startPoint = sortedPoints[0];
        features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [startPoint.longitude, startPoint.latitude]
          },
          properties: {
            id: `${track.id}_start`,
            userId: track.user_id,
            trackName: track.name,
            isActive: track.isActive,
            startedAt: track.startedAt,
            type: 'track_start'
          }
        });

        // Add end point marker (only if track is not active)
        if (!track.isActive && track.endedAt) {
          const endPoint = sortedPoints[sortedPoints.length - 1];
          features.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [endPoint.longitude, endPoint.latitude]
            },
            properties: {
              id: `${track.id}_end`,
              userId: track.user_id,
              trackName: track.name,
              endedAt: track.endedAt,
              type: 'track_end'
            }
          });
        }
      }
    });

    if (features.length > 0) {
      setTracksGeoJSON({
        type: 'FeatureCollection',
        features
      });
    } else {
      setTracksGeoJSON(null);
    }
  }, [tracks, trackPoints]);

  return {
    tracks,
    tracksGeoJSON,
    handleTracksResponse,
    handleTrackPointsResponse,
    handleUserLocationUpdate,
    handleUserStoppedTracking
  };
};